import { DateTime } from 'luxon';
import { CalendarPlanningShift } from '../calendar-shift/calendar-planning-shift.types';

export type CalendarShiftColumnSlot = CalendarPlanningShift | null;

const getDayKey = (isoDateTime: string): string => DateTime.fromISO(isoDateTime).toFormat('dd-LL');

type ShiftGroup = {
  id: string;
  shifts: CalendarPlanningShift[];
  sortKey: number;
};

const buildShiftGroups = (shifts: CalendarPlanningShift[]): ShiftGroup[] => {
  const groups: ShiftGroup[] = [];
  const multiDayGroups = new Map<string, CalendarPlanningShift[]>();

  for (const shift of shifts) {
    if (shift.multiDayShiftId == null) {
      continue;
    }
    const existing = multiDayGroups.get(shift.multiDayShiftId) ?? [];
    existing.push(shift);
    multiDayGroups.set(shift.multiDayShiftId, existing);
  }

  for (const [groupId, groupShifts] of multiDayGroups) {
    const sortedShifts = [...groupShifts].sort(
      (left, right) =>
        DateTime.fromISO(left.dateTime).toMillis() - DateTime.fromISO(right.dateTime).toMillis()
    );
    groups.push({
      id: groupId,
      shifts: sortedShifts,
      sortKey: DateTime.fromISO(sortedShifts[0].dateTime).toMillis(),
    });
  }

  for (const shift of shifts) {
    if (shift.multiDayShiftId != null) {
      continue;
    }
    groups.push({
      id: shift.id,
      shifts: [shift],
      sortKey: DateTime.fromISO(shift.dateTime).toMillis(),
    });
  }

  return groups.sort((left, right) => {
    const sortKeyDifference = left.sortKey - right.sortKey;
    if (sortKeyDifference !== 0) {
      return sortKeyDifference;
    }

    const multiDayGroupDifference = right.shifts.length - left.shifts.length;
    if (multiDayGroupDifference !== 0) {
      return multiDayGroupDifference;
    }

    return left.id.localeCompare(right.id);
  });
};

const canPlaceGroupAtRow = (
  group: ShiftGroup,
  rowIndex: number,
  columnsByDayKey: Record<string, CalendarShiftColumnSlot[]>,
  visibleDayKeys: Set<string>
): boolean =>
  group.shifts.every((shift) => {
    const dayKey = getDayKey(shift.dateTime);
    if (!visibleDayKeys.has(dayKey)) {
      return true;
    }
    const column = columnsByDayKey[dayKey];
    const slot = column[rowIndex];
    return slot == null;
  });

const placeGroupAtRow = (
  group: ShiftGroup,
  rowIndex: number,
  columnsByDayKey: Record<string, CalendarShiftColumnSlot[]>,
  visibleDayKeys: Set<string>
): void => {
  for (const shift of group.shifts) {
    const dayKey = getDayKey(shift.dateTime);
    if (!visibleDayKeys.has(dayKey)) {
      continue;
    }
    const column = columnsByDayKey[dayKey];
    while (column.length <= rowIndex) {
      column.push(null);
    }
    column[rowIndex] = shift;
  }
};

export const buildCalendarShiftColumnLayout = (
  shifts: CalendarPlanningShift[],
  visibleDays: string[]
): Record<string, CalendarShiftColumnSlot[]> => {
  const visibleDayKeys = new Set(visibleDays.map((day) => getDayKey(day)));
  const columnsByDayKey: Record<string, CalendarShiftColumnSlot[]> = {};

  for (const day of visibleDays) {
    columnsByDayKey[getDayKey(day)] = [];
  }

  const groups = buildShiftGroups(shifts);

  for (const group of groups) {
    let rowIndex = 0;
    while (!canPlaceGroupAtRow(group, rowIndex, columnsByDayKey, visibleDayKeys)) {
      rowIndex += 1;
    }
    placeGroupAtRow(group, rowIndex, columnsByDayKey, visibleDayKeys);
  }

  const maxRowCount = Math.max(0, ...Object.values(columnsByDayKey).map((column) => column.length));

  for (const dayKey of Object.keys(columnsByDayKey)) {
    const column = columnsByDayKey[dayKey];
    while (column.length < maxRowCount) {
      column.push(null);
    }
  }

  return columnsByDayKey;
};
